/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface RoomSelection {
  room: any;
  roomType: string;
  roomCode: string;
  qtyAdults: number; // Cambiado de adults a qtyAdults
  qtyChilds: number; // Cambiado de children a qtyChilds
  childsage: number[]; // Cambiado de childrenAges a childsage
  satisfiedConfigIndex?: number;
}

export interface HotelRoomState {
  originalConfiguration: any[];
  selectedRooms: RoomSelection[];
  remainingConfigs: any[];
  satisfiedConfigs: any[];
}

export interface ConfigSelectedRoomChangeEvent {
  action: 'added' | 'removed';
  groupedKey: string;
  occupancyRoom: number;
  option: any;
  roomCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class HotelService {
  private readonly STORAGE_KEY = 'hotel_rooms_state';

  private stateSubject = new BehaviorSubject<HotelRoomState | null>(this.getStoredState());
  public state$ = this.stateSubject.asObservable();
   private detailOpenState = new BehaviorSubject<{ [key: string]: boolean }>({});
  detailOpenState$ = this.detailOpenState.asObservable();

  private configSelectedRoom: { [key: string]: any[] } = {};

  private configSelectedRoomChangeSubject = new BehaviorSubject<ConfigSelectedRoomChangeEvent | null>(null);
  configSelectedRoomChange$ = this.configSelectedRoomChangeSubject.asObservable();

  /**
   * Inicializa el estado basado en la configuración original
   */
  initializeSelection(formData: any): void {
    if (!formData) {
      console.error('No se encontró roomConfiguration en formData');
      return;
    }

    const roomConfig = formData;

    const state: HotelRoomState = {
      originalConfiguration: roomConfig,
      selectedRooms: [],
      remainingConfigs: [...roomConfig],
      satisfiedConfigs: []
    };

    this.saveState(state);
    this.stateSubject.next(state);
  }

   private detailData = new BehaviorSubject<{
    roomCode: string;
    index: number;
    isOpen: boolean;
  } | null>(null);
  detailData$ = this.detailData.asObservable();

  /**
   * Agrega una habitación seleccionada
   */
  addSelectedRoom(room: any): boolean {
    const current = this.stateSubject.value;
    if (!current) return false;

    const roomAdults = Number(room.adults) || 0;
    const roomChildren = Number(room.childrens) || 0;

    // Buscar una configuración que coincida con esta habitación
    const configIndex = this.findMatchingConfig(current.remainingConfigs, roomAdults, roomChildren);

    if (configIndex === -1) {
      console.warn('No hay configuración disponible que coincida con esta habitación');
      return false;
    }

    const matchedConfig = current.remainingConfigs[configIndex];

    // Crear la selección con los nombres correctos
    const roomSelection: RoomSelection = {
      room: room,
      roomType: room.roomType,
      roomCode: room.roomCode,
      qtyAdults: roomAdults,
      qtyChilds: roomChildren,
      childsage: matchedConfig.children || matchedConfig.childsage || [],
      satisfiedConfigIndex: configIndex
    };

    // Actualizar estado
    const newRemainingConfigs = [...current.remainingConfigs];
    const [satisfiedConfig] = newRemainingConfigs.splice(configIndex, 1);

    const newState: HotelRoomState = {
      ...current,
      selectedRooms: [...current.selectedRooms, roomSelection],
      remainingConfigs: newRemainingConfigs,
      satisfiedConfigs: [...current.satisfiedConfigs, satisfiedConfig]
    };

    this.saveState(newState);
    this.stateSubject.next(newState);
    return true;
  }

  /**
   * Encuentra una configuración que coincida con los parámetros de la habitación
   */
  private findMatchingConfig(configs: any[], roomAdults: number, roomChildren: number): number {
    return configs.findIndex(config => {
      // Usar qtyAdults y qtyChilds para la comparación
      const configAdults = Number(config.qtyAdults) || config.adults || 0;
      const configChildren = Number(config.qtyChilds) || config.children?.length || 0;

      return configAdults === roomAdults && configChildren === roomChildren;
    });
  }

  /**
   * Remueve una habitación seleccionada
   */
  /**
   * Remueve una habitación seleccionada
   */
  removeSelectedRoom(item: any): void {
    const current = this.stateSubject.value;
    if (!current) return;

    // Buscar por productKey que es único
    const roomToRemove = current.selectedRooms.find(selection =>
      selection.room.productKey === item.productKey
    );

    if (!roomToRemove) {
      console.warn('Room to remove NOT FOUND in selectedRooms');
      return;
    }

    // Usar satisfiedConfigIndex que ya tenemos guardado
    if (roomToRemove.satisfiedConfigIndex !== undefined && roomToRemove.satisfiedConfigIndex < current.satisfiedConfigs.length) {
      const configToRestore = current.satisfiedConfigs[roomToRemove.satisfiedConfigIndex];

      const newState: HotelRoomState = {
        ...current,
        selectedRooms: current.selectedRooms.filter(selection =>
          selection.room.productKey !== item.productKey
        ),
        remainingConfigs: [...current.remainingConfigs, configToRestore],
        satisfiedConfigs: current.satisfiedConfigs.filter((_, index) => index !== roomToRemove.satisfiedConfigIndex)
      };

      this.saveState(newState);
      this.stateSubject.next(newState);
    }
  }

  /**
   * Verifica si se completó la selección
   */
  isSelectionComplete(): boolean {
    const current = this.stateSubject.value;
    if (!current) return false;

    return current.remainingConfigs.length === 0;
  }

  /**
   * Obtiene todas las habitaciones seleccionadas
   */
  getSelectedRooms(): any[] {
    const current = this.stateSubject.value;
    return current?.selectedRooms.map(selection => selection.room) || [];
  }

  /**
   * Obtiene las configuraciones que aún necesitan habitación
   */
  getRemainingConfigs(): any[] {
    const current = this.stateSubject.value;
    return current?.remainingConfigs || [];
  }

  /**
   * Obtiene las configuraciones que ya tienen habitación
   */
  getSatisfiedConfigs(): any[] {
    const current = this.stateSubject.value;
    return current?.satisfiedConfigs || [];
  }

  /**
   * Obtiene el estado completo
   */
  getCurrentState(): HotelRoomState | null {
    return this.stateSubject.value;
  }

  /**
   * Verifica si una habitación puede satisfacer alguna configuración restante
   */
  canSelectRoom(room: any): boolean {
    const current = this.stateSubject.value;
    if (!current) return false;

    const roomAdults = room.adults || 0;
    const roomChildren = room.childrens || 0;

    return this.findMatchingConfig(current.remainingConfigs, roomAdults, roomChildren) !== -1;
  }

  /**
   * Obtiene las configuraciones que coinciden con una habitación específica
   */
  getMatchingConfigs(room: any): any[] {
    const current = this.stateSubject.value;
    if (!current) return [];

    const roomAdults = room.adults || 0;
    const roomChildren = room.childrens || 0;

    return current.remainingConfigs.filter(config => {
      const configAdults = config.qtyAdults || config.adults || 0;
      const configChildren = config.qtyChilds || config.children?.length || 0;

      return configAdults === roomAdults && configChildren === roomChildren;
    });
  }

  // Persistencia
  private saveState(state: HotelRoomState): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private getStoredState(): HotelRoomState | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  clearState(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.stateSubject.next(null);
  }


  // Seleccionar opciones de habitación

getConfigSelectedRoom(): { [key: string]: any[] } {
  return this.configSelectedRoom;
}

getAllSelectedOccupancyRooms(): { occupancyRoom: number, groupedKey: string, option: any }[] {
  const result: { occupancyRoom: number, groupedKey: string, option: any }[] = [];
  Object.keys(this.configSelectedRoom).forEach(key => {
    this.configSelectedRoom[key].forEach(item => {
      if (item?.room?.occupancyRoom) {
        result.push({
          occupancyRoom: item.room.occupancyRoom,
          groupedKey: key,
          option: item
        });
      }
    });
  });
  return result;
}

isOccupancyRoomSelected(occupancyRoom: number): boolean {
  return this.getAllSelectedOccupancyRooms().some(item => item.occupancyRoom === occupancyRoom);
}

getGroupKeyForOccupancyRoom(occupancyRoom: number): string | null {
  const found = this.getAllSelectedOccupancyRooms().find(item => item.occupancyRoom === occupancyRoom);
  return found ? found.groupedKey : null;
}

toggleConfigSelectedRoom(groupedKey: string, option: any, roomCode?: string, silent = false): boolean {

  const occupancyRoom = option?.room?.occupancyRoom;

  // ✅ Verificar si ya existe en algún grupo
  const existingItems = this.getAllSelectedOccupancyRooms();
  const existing = existingItems.find(item => item.occupancyRoom === occupancyRoom);

  if (existing) {
    // ✅ Si existe, lo removemos (desactivar)
    this.removeFromGroup(existing.groupedKey, occupancyRoom);
    if (!silent) {
      this.emitConfigSelectedRoomChange({
        action: 'removed',
        groupedKey: existing.groupedKey,
        occupancyRoom,
        option,
        roomCode
      });
    }
    return false; // ❌ Fue desactivado
  } else {

    // ✅ Si no existe, lo agregamos (activar)
    if (!this.configSelectedRoom[groupedKey]) {
      this.configSelectedRoom[groupedKey] = [];
    }
    this.configSelectedRoom[groupedKey].push(option);
    if (!silent) {
      this.emitConfigSelectedRoomChange({
        action: 'added',
        groupedKey,
        occupancyRoom,
        option,
        roomCode
      });
    }
    return true; // ✅ Fue activado
  }
}

private emitConfigSelectedRoomChange(event: ConfigSelectedRoomChangeEvent): void {
  this.configSelectedRoomChangeSubject.next(event);
}

private removeFromGroup(groupedKey: string, occupancyRoom: number): void {
  if (!this.configSelectedRoom[groupedKey]) return;

  this.configSelectedRoom[groupedKey] = this.configSelectedRoom[groupedKey].filter(
    item => item.room?.occupancyRoom !== occupancyRoom
  );

  if (this.configSelectedRoom[groupedKey].length === 0) {
    delete this.configSelectedRoom[groupedKey];
  }
}

clearAllSelectedRooms(): void {
  this.configSelectedRoom = {};
  this.configSelectedRoomChangeSubject.next(null);
}

/**
 * ✅ Alternar el detalle de una habitación (solo notifica abrir/cerrar)
 * @param index - Índice de la habitación
 * @param room - Datos de la habitación
 * @param roomCode - Código único de la habitación
 */
toggleRoomDetail(index: number, room: any, roomCode: string): void {
  const currentState = this.detailOpenState.value;
  const detailId = roomCode;

  // ✅ Si ya está abierto, cerrarlo
  if (currentState[detailId]) {
    this.detailOpenState.next({
      ...currentState,
      [detailId]: false
    });
    this.detailData.next(null);
    return;
  }

  // ✅ Cerrar todos los demás detalles
  const newState: { [key: string]: boolean } = {};
  Object.keys(currentState).forEach(key => {
    newState[key] = false;
  });

  // ✅ Abrir el nuevo detalle
  newState[detailId] = true;
  this.detailOpenState.next(newState);

  // ✅ Notificar que se abrió (sin datos adicionales)
  this.detailData.next({
    roomCode: roomCode,
    index: index,
    isOpen: true
  });
}

  /**
   * ✅ Verificar si el detalle de una habitación está abierto
   */
  isRoomDetailOpen(roomCode: string): boolean {
    return this.detailOpenState.value[roomCode] || false;
  }

  /**
   * ✅ Obtener los datos del detalle de una habitación
   */
  getRoomDetailData(roomCode: string): any {
    const data = this.detailData.value;
    return data && data.roomCode === roomCode ? data : null;
  }

  /**
   * ✅ Cerrar todos los detalles
   */
  closeAllDetails(): void {
    this.detailOpenState.next({});
    this.detailData.next(null);
  }

  // ✅ Métodos auxiliares para obtener datos del detalle
  private getCancellationDetailPolicies(room: any): any {
    return {
      rateClass: room?.room?.rateClass,
      cancellationPolicies: room?.room?.cancellationPolicy || [],
      currencyCancellation: room?.room?.cancellationPolicy && room.room?.cancellationPolicy.length > 0 ? room.room?.cancellationPolicy[0].currency : room.room?.pricing?.currency,
      amount: room?.room?.cancellationPolicy?.find(c => c.amount > 0)?.amountProf || 0,
      from: room?.room?.cancellationPolicy?.find(c => c.amount > 0)?.from || 'N/A',
      cancellationFree: room?.room?.cancellationPolicy?.some(c => Number(c.amount) === 0),
      // arrivalDate: this.arrivalDate
    }
  }

  private getAccordionDetailItems(room: any): any[] {
   return [
    {
      id: 'policies',
      title: 'Políticas de Cancelación y Penalidades',
      icon: 'assets/icons/policy.svg',
      data: { policies: this.getCancellationDetailPolicies(room) } // NOTA: aquí también debes cambiar
    }
  ];
  }

  private getAccordionDetailConfig(): any {
    // Tu lógica para obtener configuración del acordeón
    return {};
  }

}
